import { useCallback, useEffect, useRef, useState } from 'react';
import { MORE_ACTIONS } from '../../lib/copy';
import './overflow-menu.css';

/**
 * **The head's actions, folded behind one glyph.**
 *
 * A panel head has a name, the line that says what the thing is, and — on an
 * organisation — three things the office can do to it. Written out in a row they
 * took more width than the column has: the actions were cut off mid-word at the
 * panel's edge ("TRACK THIS ORGANISAT…") and the name they were competing with
 * was squeezed until its standing line broke one word to a line. The name is
 * what the reader came for, so the name gets the width and the actions get a
 * button.
 *
 * It is a menu button in the ARIA sense (`aria-haspopup="menu"`, a `role="menu"`
 * of `role="menuitem"` children) rather than the org switcher's plain buttons in
 * a box, because that is exactly what this is: a closed list of one-press
 * commands, none of which is a setting with a current value. Where the record
 * head's status control uses `menuitemradio` — those are the values of one
 * setting, one of which is on — these are commands, and `menuitem` is what says
 * so.
 *
 * **Only single-press actions belong in here.** A control that opens something
 * of its own — the person Track door, which replaces itself with a picker of the
 * vertical's words and needs three ways back out — would be a menu that stays
 * open around a second menu, and the reader's way out of the inner one would
 * close the outer. Those stay on the head where they are.
 *
 * **The list is in the markup whether or not it is open**, hidden rather than
 * unmounted. Two reasons, and they agree: this package's suite has no DOM and
 * asserts on `renderToStaticMarkup`, so an unmounted list could not be asserted
 * at all; and the items are the head's own offer — which of them exists is a
 * fact about the record, not about whether somebody has pressed the glyph.
 *
 * The caller owns the items, so a link item can be a real `Link` and a write
 * item can carry its own busy word. All this owns is the opening, the closing
 * and the caret: press an item and it shuts, Escape shuts it and hands the caret
 * back, a click anywhere else shuts it and leaves the caret where the reader put
 * it, and the arrows walk whatever `role="menuitem"` children it was handed.
 */
export function OverflowMenu({
  label = MORE_ACTIONS,
  children,
}: {
  /** The button's accessible name, and the menu's. */
  label?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  /* Which end the arrows opened it from: ArrowUp opens onto the last item, the
     way every menu button does, so a reader reaching for the bottom of a short
     list does not walk the whole of it. */
  const [fromEnd, setFromEnd] = useState(false);

  const root = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  /** The items as they actually are, which is the caller's business, not ours. */
  const itemsOf = useCallback(
    (): HTMLElement[] =>
      Array.from(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []),
    [],
  );

  const close = useCallback((focusTrigger: boolean) => {
    setOpen(false);
    // Escape and a chosen item are the reader coming back to the control they
    // opened; a click elsewhere has already chosen somewhere else to be.
    if (focusTrigger) trigger.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      // A dialog's Escape is the dialog's — the rule every other layer keeps.
      if (document.querySelector('[aria-modal="true"]')) return;
      if (event.key === 'Escape') close(true);
    };
    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) close(false);
    };

    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, close]);

  /* Opening puts the caret in the list. The items are in the document either
     way, so this is the only thing that has to wait for `open` — and a hidden
     subtree cannot hold focus, so it has to wait for it here rather than in the
     press. */
  useEffect(() => {
    if (!open) return;
    const items = itemsOf();
    (fromEnd ? items[items.length - 1] : items[0])?.focus();
  }, [open, fromEnd, itemsOf]);

  const openAt = (end: boolean) => {
    setFromEnd(end);
    setOpen(true);
  };

  const move = (delta: number) => {
    const items = itemsOf();
    if (items.length === 0) return;
    const at = items.indexOf(document.activeElement as HTMLElement);
    items[(Math.max(at, 0) + delta + items.length) % items.length]?.focus();
  };

  return (
    <div className="overflow-menu" ref={root}>
      <button
        type="button"
        className="overflow-menu-trigger"
        ref={trigger}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? close(false) : openAt(false))}
        onKeyDown={(event) => {
          /* Enter and Space are the button's own press and already open it.
             The arrows are the menu button's extra promise: down onto the
             first item, up onto the last. */
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            openAt(event.key === 'ArrowUp');
          }
        }}
      >
        <span aria-hidden="true">…</span>
      </button>

      <div
        className="overflow-menu-list"
        role="menu"
        aria-label={label}
        hidden={!open}
        ref={menu}
        onClick={(event) => {
          const item = (event.target as HTMLElement).closest('[role="menuitem"]');
          if (item === null) return;
          /* A chosen command comes back to the glyph. A chosen *link* does not:
             it is on its way to another screen, and snatching the caret back on
             the way out would be the app arguing with the press. */
          close(item.tagName !== 'A');
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            move(1);
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            move(-1);
          } else if (event.key === 'Home') {
            event.preventDefault();
            itemsOf()[0]?.focus();
          } else if (event.key === 'End') {
            event.preventDefault();
            const items = itemsOf();
            items[items.length - 1]?.focus();
          } else if (event.key === 'Tab') {
            /* Tabbing out of a menu closes it, and the caret goes back to the
               glyph *first*: the browser computes the next stop from where the
               focus is when the default runs, and the item it is on is about to
               be inside a `hidden` subtree. Restoring focus to the trigger makes
               the next stop the one after the trigger — which is where a reader
               tabbing past a shut menu expects to land — instead of the top of
               the document, where focus falls when its holder disappears. */
            close(true);
          }
        }}
      >
        {children}
      </div>
    </div>
  );
}
